import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Download, Search, PencilLine, Building2 } from 'lucide-react';
import FranchiseChangeDialog from '@/components/FranchiseChangeDialog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

export default function DeliveryChallanEditHistory() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filters, setFilters] = React.useState({ dc_number: '', edited_by: '', start_date: '', end_date: '' });
  const [canEdit, setCanEdit] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [dcNo, setDcNo] = React.useState('');
  const [dc, setDc] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showFranchise, setShowFranchise] = React.useState(false);
  const isAdmin = (() => { try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '') === 'admin'; } catch { return false; } })();

  const load = React.useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/dc-edit-history?' + p, auth())
      .then((r) => setRows(r.data.history))
      .catch((e) => toast.error(e.response?.data?.detail || 'Load failed'))
      .finally(() => setLoading(false));
  }, [filters]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    axios.get(API + '/accounts/dc-edit-permission', auth())
      .then((r) => setCanEdit(r.data.can_edit_dispatched)).catch(() => {});
  }, []);

  const lookup = () => {
    axios.get(API + '/accounts/dc-lookup/' + dcNo.trim(), auth())
      .then((r) => {
        if (r.data.dc.status !== 'dispatched') {
          return toast.error('Only DISPATCHED challans can be edited here (status: ' + r.data.dc.status + ')');
        }
        setDc(r.data.dc);
        setItems(r.data.dc.items.map((it) => ({ ...it })));
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'DC not found'));
  };

  const setItem = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    setItems(next);
  };

  const liveTotal = items.reduce((a, it) => a + (Number(it.quantity) || 0) * (Number(it.selling_price) || 0), 0);

  const save = () => {
    if (!reason.trim()) return toast.error('Reason for edit is mandatory');
    setBusy(true);
    axios.put(API + `/accounts/delivery-challans/${dc.id}/dispatched-edit`, {
      reason,
      items: items.map((it) => ({
        product_id: it.product_id,
        quantity: Number(it.quantity),
        mrp: Number(it.mrp || 0),
        selling_price: Number(it.selling_price || 0),
      })),
    }, auth())
      .then((r) => { toast.success(`Saved — version ${r.data.version}, new value ₹${r.data.total_value}`); setShowEdit(false); setDc(null); setDcNo(''); setReason(''); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Save failed'))
      .finally(() => setBusy(false));
  };

  const exportXlsx = () => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/dc-edit-history/export.xlsx?' + p, { ...auth(), responseType: 'blob' })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = Object.assign(document.createElement('a'), { href: url, download: 'dc-edit-history.xlsx' });
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export failed'));
  };

  return (
    <div className="p-6 space-y-4" data-testid="dc-edit-history-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Delivery Challan Edit History</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportXlsx} data-testid="dceh-export-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
          <CsvDownloadButton onDownload={exportXlsx} testId="dceh-export-csv" size="default" />
          {isAdmin && <Button variant="outline" onClick={() => setShowFranchise(true)} data-testid="dceh-change-franchise-btn"><Building2 className="w-4 h-4 mr-1" />Change Franchise</Button>}
          {canEdit && <Button onClick={() => setShowEdit(true)} data-testid="dceh-edit-dc-btn"><PencilLine className="w-4 h-4 mr-1" />Edit Dispatched DC</Button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input placeholder="DC No" className="h-9 w-40" value={filters.dc_number} onChange={(e) => setFilters({ ...filters, dc_number: e.target.value })} data-testid="dceh-dc-filter" />
        <Input placeholder="Edited By" className="h-9 w-40" value={filters.edited_by} onChange={(e) => setFilters({ ...filters, edited_by: e.target.value })} data-testid="dceh-editedby-filter" />
        <Input type="date" className="h-9 w-36" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="dceh-start-date" />
        <Input type="date" className="h-9 w-36" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="dceh-end-date" />
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="dceh-table">
            <thead><tr className="border-b bg-slate-50 text-left">
              <th className="p-2">DC No</th><th className="p-2">Version</th><th className="p-2">Edited By</th>
              <th className="p-2">Date & Time</th><th className="p-2">Reason</th>
              <th className="p-2 text-right">Prev Value</th><th className="p-2 text-right">New Value</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No DC edits recorded</td></tr> : rows.map((h) => (
                <tr key={h.id} className="border-b hover:bg-slate-50" data-testid={'dceh-row-' + h.dc_number + '-v' + h.version}>
                  <td className="p-2 font-mono">{h.dc_number}</td>
                  <td className="p-2">v{h.version}</td>
                  <td className="p-2">{h.edited_by_name}</td>
                  <td className="p-2 text-xs">{(h.edited_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-2 text-xs max-w-[240px]">{h.reason}</td>
                  <td className="p-2 text-right">{h.previous?.total_value}</td>
                  <td className="p-2 text-right font-semibold">{h.new?.total_value}</td>
                </tr>))}
            </tbody>
          </table>
        </div>)}

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dceh-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Dispatched Delivery Challan</DialogTitle>
            <DialogDescription>Only Quantity, MRP and Selling Price can be changed. Every edit is audit-logged and quantity changes sync inventory.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="DC Number (e.g. DC-2026-000123)" value={dcNo} onChange={(e) => setDcNo(e.target.value)} data-testid="dceh-lookup-input" />
            <Button onClick={lookup} data-testid="dceh-lookup-btn"><Search className="w-4 h-4 mr-1" />Load</Button>
          </div>
          {dc && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{dc.to_location} · {dc.dc_number} · v{dc.edit_version || 0} · Current value ₹{dc.total_value}</p>
              <p className="text-xs text-slate-400">Editable: Quantity, MRP and Selling Price only. Quantity changes sync inventory automatically.</p>
              <table className="w-full text-xs border rounded">
                <thead><tr className="bg-slate-50 text-left"><th className="p-2">Product</th><th className="p-2">Qty</th><th className="p-2">MRP</th><th className="p-2">Selling Price</th><th className="p-2 text-right">Line Total</th></tr></thead>
                <tbody>{items.map((it, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2">{it.product_name}<div className="text-slate-400 font-mono">{it.product_sku}</div></td>
                    <td className="p-2"><Input type="number" className="h-8 w-20" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} data-testid={'dceh-qty-' + i} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-24" value={it.mrp || ''} onChange={(e) => setItem(i, 'mrp', e.target.value)} data-testid={'dceh-mrp-' + i} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-24" value={it.selling_price || ''} onChange={(e) => setItem(i, 'selling_price', e.target.value)} data-testid={'dceh-price-' + i} /></td>
                    <td className="p-2 text-right font-medium">{((Number(it.quantity) || 0) * (Number(it.selling_price) || 0)).toFixed(2)}</td>
                  </tr>))}</tbody>
                <tfoot><tr className="bg-slate-50 font-semibold"><td className="p-2" colSpan={4}>New DC Value</td><td className="p-2 text-right" data-testid="dceh-live-total">₹{liveTotal.toFixed(2)}</td></tr></tfoot>
              </table>
              <Textarea placeholder="Reason for edit (mandatory)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dceh-reason-input" />
              <Button onClick={save} disabled={busy} data-testid="dceh-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
            </div>)}
        </DialogContent>
      </Dialog>
      <FranchiseChangeDialog entity="delivery_challan" open={showFranchise} onOpenChange={setShowFranchise} />
    </div>
  );
}
